import { createContext, useContext } from "react";

import type { TenantDirectoryEntry } from "@glonextrack/contracts";

export interface TenantSettings {
  theme?: Record<string, unknown>;
  map?: Record<string, unknown>;
  ai?: Record<string, unknown>;
  alerts?: Record<string, unknown>;
  devices?: Record<string, unknown>;
  cameras?: Record<string, unknown>;
  telemetry?: Record<string, unknown>;
  retention?: Record<string, unknown>;
}

export interface TenantBillingAccessState {
  status: "active" | "grace" | "warning" | "restricted" | "suspended";
  source: "healthy" | "delinquency" | "manual_override";
  reason?: string | null;
  customerMessage?: string | null;
  graceUntil?: string | null;
  disabledModules?: string[];
  billingOnly?: boolean;
  overdueInvoiceCount?: number;
  outstandingAmount?: number;
  currency?: string | null;
  highestDaysPastDue?: number;
  highestStage?: string | null;
  actionable?: boolean;
}

export interface SessionState {
  userId: string;
  tenantId?: string;
  role?: string;
  permissions: string[];
  tenants?: TenantDirectoryEntry[];
  accountId?: string;
  // Enriched user profile
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  userStatus?: string | null;
  // Enriched tenant context
  tenantName?: string | null;
  tenantSlug?: string | null;
  tenantPlan?: string | null;
  tenantStatus?: string | null;
  tenantIndustry?: string | null;
  tenantPackage?: "core" | "insurance" | "farming" | null;
  enabledServices?: string[];
  enabledModules?: string[];
  moduleAccess?: Record<string, boolean>;
  tenantSettings?: TenantSettings | null;
  /** From TenantSettings.subUserBilling.defaultCurrency (ISO 4217) */
  defaultBillingCurrency?: string;
  tenantBillingAccess?: TenantBillingAccessState | null;
}

export type SessionStatus = "idle" | "loading" | "ready" | "error";

export interface SessionContextValue extends SessionState {
  status: SessionStatus;
  error?: string;
  updateSession: (next: Partial<SessionState>) => void;
  setStatus: (status: SessionStatus, error?: string) => void;
  refreshSession: (options?: { force?: boolean }) => Promise<boolean>;
}

const defaultSessionState: SessionContextValue = {
  userId: "",
  tenantId: undefined,
  role: undefined,
  permissions: [],
  tenants: [],
  accountId: undefined,
  status: "idle",
  error: undefined,
  updateSession: () => undefined,
  setStatus: () => undefined,
  refreshSession: async () => false,
};

export const SessionContext = createContext<SessionContextValue>(defaultSessionState);

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}
